import { z } from "zod";

import { OutputType } from "../../enums.js";
import { AppSessionCellId, OutputContentId } from "../../idTypeBrands.js";
import { zAppSessionCellId } from "../../zod/ZodIdTypeBrands.js";
import { zReadonlyArray } from "../../zod/utils.js";
import { createAppSessionEventDefinition } from "../AppSessionEventDefinition.js";

import { DELETE_OUTPUT } from "./DELETE_OUTPUT.js";

export const CLEAR_OUTPUTS = createAppSessionEventDefinition({
  type: "CLEAR_OUTPUTS",
  payloadSchema: z.object({
    appSessionCellId: zAppSessionCellId,
    requestId: z.string().optional(),
    preserveMimeTypes: zReadonlyArray(z.string()).optional(),
    clearMetadata: z.boolean().optional(),
    clearNonMetadata: z.boolean().optional(),
  }),
  logSafe: {
    appSessionCellId: true,
    requestId: true,
    preserveMimeTypes: true,
    clearMetadata: true,
    clearNonMetadata: true,
  },
  create: (payload: {
    appSessionCellId: AppSessionCellId;
    /**
     * If present, skip deleting outputs with this `requestId`.
     * Generally used to clear outputs from previous runs without affecting
     * the 'current' outputs.
     *
     * (This param probably should have been called `preserveRequestId`)
     */
    requestId?: string;
    /**
     * If present, skip deleting any outputs that have a content with this mime type
     */
    preserveMimeTypes?: readonly string[];
    /** @default true */
    clearMetadata?: boolean;
    /** @default true */
    clearNonMetadata?: boolean;
  }) => payload,
  apply: ({
    indices,
    payload: {
      appSessionCellId,
      clearMetadata = true,
      clearNonMetadata = true,
      preserveMimeTypes,
      requestId,
    },
    state,
  }) => {
    const warnings: string[] = [];

    const preserveMimeTypesSet = new Set(preserveMimeTypes);

    const outputIds = indices.appSessionCellIdToOutputIds[appSessionCellId];

    if (outputIds == null) {
      warnings.push(
        `Output index for app session cell not found: ${appSessionCellId}`,
      );
    }

    outputs: for (const outputId of outputIds ?? []) {
      const output = state.outputs[outputId];

      if (output == null) {
        warnings.push(`Output not found: ${outputId}`);
        continue outputs;
      }

      // If requestId is present, we want to preserve any associated outputs
      if (requestId != null && output.requestId === requestId) {
        continue outputs;
      }

      const isMetadata = output.outputType === OutputType.METADATA;

      if (
        (isMetadata && !clearMetadata) ||
        (!isMetadata && !clearNonMetadata)
      ) {
        continue outputs;
      }

      const outputContentIds = indices.outputIdToOutputContentIds[output.id];

      if (outputContentIds == null) {
        warnings.push(
          `Output content index for output not found: ${output.id}`,
        );
      }

      let deleteOutput = true;
      const outputContentIdsToDelete: Set<OutputContentId> = new Set();

      contents: for (const outputContentId of outputContentIds ?? []) {
        const outputContent = state.outputContents[outputContentId];

        if (outputContent == null) {
          warnings.push(`Output content not found: ${outputContentId}`);
          continue contents;
        }

        if (preserveMimeTypesSet.has(outputContent.mimeType)) {
          deleteOutput = false;
          continue contents;
        }

        outputContentIdsToDelete.add(outputContentId);
      }

      if (deleteOutput) {
        DELETE_OUTPUT.apply({ state, indices, payload: { outputId } });
      } else {
        for (const outputContentId of outputContentIdsToDelete) {
          delete state.outputContents[outputContentId];
        }
        indices.outputIdToOutputContentIds[outputId] =
          outputContentIds?.filter((id) => !outputContentIdsToDelete.has(id)) ??
          [];
      }
    }

    return { warnings };
  },
});
